import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

const UNITS_PREFERENCE_KEY = '@flavrly/units-preference';

export type UnitSystem = 'imperial' | 'metric';

/**
 * Hook to manage unit system preference with AsyncStorage persistence.
 */
export function useUnitsPreference() {
  const [units, setUnitsState] = useState<UnitSystem>('imperial');
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from AsyncStorage on mount
  useEffect(() => {
    const loadPreference = async () => {
      try {
        const stored = await AsyncStorage.getItem(UNITS_PREFERENCE_KEY);
        if (stored === 'imperial' || stored === 'metric') {
          setUnitsState(stored);
        }
      } catch (error) {
        console.error('Failed to load units preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreference();
  }, []);

  const setUnits = useCallback(async (newUnits: UnitSystem) => {
    try {
      await AsyncStorage.setItem(UNITS_PREFERENCE_KEY, newUnits);
      setUnitsState(newUnits);
    } catch (error) {
      console.error('Failed to save units preference:', error);
    }
  }, []);

  const toggleUnits = useCallback(async () => {
    const newUnits: UnitSystem = units === 'imperial' ? 'metric' : 'imperial';
    await setUnits(newUnits);
  }, [units, setUnits]);

  return {
    units,
    setUnits,
    toggleUnits,
    isLoading,
  };
}
